import React, { useState, useEffect, Fragment } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import classnames from 'classnames';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

import sidebarMap from '../../constants/sidebarMap';
import { AccessMap, IEmployee, IUser, SidebarMap } from '../../globals/interfaces';
import Card from '../custom/Card';
import ActionIcon from '../custom/ActionIcon';
import { getTsDate, notify } from '../../utils';
import useRestrictedAccess from '../../customHooks/useRestrictedAccess';
import { CardWrapper } from './SharedComponents';
import apiClient from '../../apiClient';
import { Button } from 'reactstrap';

interface Props {
    user: IUser,
    modal: boolean;
    setModal: (state: boolean) => void;
    selectedEmployee: IEmployee;
    profile?: boolean;
    afterSubmit?: () => void;
    afterDelete?: (_id: string) => void;
    selectedProfileId?: string;
}

export default function ModalAccessMap({
    user,
    modal,
    setModal,
    selectedEmployee,
    profile,
    afterSubmit,
    afterDelete,
    selectedProfileId
}: Props) {
    const toggle = () => setModal(!modal);

    const [userAccessMap, setUserAccessMap] = useState<SidebarMap>({});
    const [accessMap, setAccessMap] = useState<object>();
    const [profiles, setProfiles] = useState<Array<AccessMap>>([]);
    const [viewProfiles, setViewProfiles] = useState<boolean>(false);
    const restirctedAccess = useRestrictedAccess(user.s_email);
    const [profileId, setProfileId] = useState('');

    useEffect(() => {
        const getProfiles = async () => {
            const res = await apiClient.get('/accessMapProfiles');
            setProfiles(res.data);
        }
        if (modal) {
            getProfiles();
        }
        setUserAccessMap({});
    }, [modal]);

    useEffect(() => {
        const getUserAccessMap = async () => {
            try {
                const res = await apiClient.get(`/accessMap/${selectedEmployee.s_email}`);
                setUserAccessMap(res.data);
            } catch (err) {

            }
        }
        if (modal) {
            getUserAccessMap();
        }
    }, [selectedEmployee, modal]);

    useEffect(() => {
        const sideBarMapCopy = _.cloneDeep(sidebarMap);

        for (let tabId in userAccessMap) {
            const tab = sideBarMapCopy[tabId];
            if (!tab) {
                console.warn(`Tab ${tabId} not found in sidebarMap`);
                continue;
            }

            tab.selected = true;
            delete tab.component;

            const { subs } = userAccessMap[tabId];

            for (let subId in subs || {}) {
                const sub = tab.subs?.[subId];
                if (sub) {
                    sub.selected = true;
                    delete sub.component;

                    const finalSubs = subs[subId]?.subs;

                    for (let finalSubId in finalSubs || {}) {
                        const finalSub = sub.subs?.[finalSubId];
                        if (finalSub) {
                            finalSub.selected = true;
                            delete finalSub.component;
                        }
                    }
                }
            }
        }

        delete sideBarMapCopy.dontRender;

        setAccessMap(sideBarMapCopy);
    }, [userAccessMap]);

    useEffect(() => {
        if (viewProfiles) {
            setProfileId('');
        }
    }, [viewProfiles]);

    useEffect(() => {
        setProfileId(selectedProfileId);
    }, [selectedProfileId]);

    const handleSelectTab = (tabId: string) => {
        setAccessMap((prev) => {
            const copy = { ...prev };
            copy[tabId].selected = !copy[tabId].selected;
            return copy;
        })
    }

    const selectSub = (tabId: string, subId: string) => {
        setAccessMap(prev => {
            const copy = { ...prev };
            copy[tabId].subs[subId].selected = !copy[tabId].subs[subId].selected;
            return copy;
        });
    }

    const selectFinalSub = (tabId: string, subId: string, finalSubId: string) => {
        setAccessMap(prev => {
            const copy = { ...prev };
            copy[tabId].subs[subId].subs[finalSubId].selected = !copy[tabId].subs[subId].subs[finalSubId].selected;
            return copy;
        });
    }

    const manageAccessMap = async () => {
        const copy = _.cloneDeep(accessMap);
        for (let tabId in copy) {
            if (!copy[tabId].selected) {
                delete copy[tabId];
            } else {
                const subs = copy[tabId].subs;
                for (let subId in subs) {
                    if (!subs[subId].selected) {
                        delete subs[subId];
                    } else {
                        const thirdSubs = subs[subId].subs;
                        for (let thirdSubId in thirdSubs) {
                            if (!thirdSubs[thirdSubId].selected) {
                                delete thirdSubs[thirdSubId];
                            }
                        }
                    }
                }
            }
        }

        const res = await apiClient.post('/accessMap', {
            email: selectedEmployee.s_email,
            map: copy,
            i_employee_id: selectedEmployee.id,
            s_modified_by: user.s_email,
            t_modified: getTsDate(),
            profile
        });

        if ([201, 204].indexOf(res.status) !== -1) {
            notify('Updated');
            toggle();
            afterSubmit && afterSubmit();
        }
    }

    const selectAllSubs = (tabId: string) => {
        setAccessMap(prev => {
            const copy = _.cloneDeep(prev);
            const { subs } = copy[tabId];
            for (let subId in subs) {
                subs[subId].selected = !subs[subId].selected;
                const finalSubs = subs[subId].subs;
                for (let finalSubId in finalSubs) {
                    finalSubs[finalSubId].selected = !finalSubs[finalSubId].selected;
                }
            }
            return copy;
        });
    }

    const deleteAccessMap = async (_id: string) => {
        apiClient.delete(`/accessMap/${_id}`);
        setModal(false);
        afterDelete && afterDelete(_id);
        notify('Profile deleted');
    }

    const applyProfile = (profile: AccessMap) => {
        const profileMap = profile.map;
        const sideBarMapCopy = _.cloneDeep(sidebarMap);

        for (let tabId in profileMap) {
            const tab = sideBarMapCopy[tabId];
            if (!tab) continue;

            tab.selected = true;
            delete tab.component;

            const { subs } = profileMap[tabId];
            for (let subId in subs || {}) {
                const sub = tab.subs?.[subId];
                if (sub) {
                    sub.selected = true;
                    delete sub.component;
                    const finalSubs = subs[subId]?.subs;
                    for (let finalSubId in finalSubs || {}) {
                        const finalSub = sub.subs?.[finalSubId];
                        if (finalSub) {
                            finalSub.selected = true;
                            delete finalSub.component;
                        }
                    }
                }
            }
        }

        delete sideBarMapCopy.dontRender;
        // @ts-ignore
        setAccessMap(sideBarMapCopy);
        setViewProfiles(false);
    }

    return (
        <Modal isOpen={modal} toggle={toggle} size={'lg'} className={'responsive-modal'}>
            <ModalHeader>
                <div>
                    Manage Access Map for {`${selectedEmployee.s_first_name} ${selectedEmployee.s_last_name}`}
                </div>
                {
                    !profileId && (
                        <Button onClick={() => setViewProfiles(true)}>Use Profile</Button>
                    )
                }
            </ModalHeader>
            <ModalBody>
                {
                    viewProfiles ? (
                        <FlexContainer>
                            {
                                (profiles || []).map((item, i) => (
                                    <AccessMapButton
                                        key={i}
                                        // @ts-ignore
                                        onClick={() => applyProfile(item)}
                                    >
                                        {item.email}
                                    </AccessMapButton>
                                ))
                            }
                        </FlexContainer>
                    ) : (
                        <Wrapper>
                            {
                                accessMap && Object.keys(accessMap).map((tabId: string) => (
                                    <CardWrapper
                                        selected={accessMap[tabId].selected}
                                        key={tabId}
                                    >
                                        <Header>
                                            <TitleDiv>
                                                <h1>
                                                    {accessMap[tabId].label}
                                                </h1>
                                            </TitleDiv>
                                            <CardIcon
                                                className={classnames(
                                                    getChecked(accessMap[tabId].selected)
                                                )}
                                                onClick={() => handleSelectTab(tabId)}
                                            />
                                            {
                                                accessMap[tabId].selected &&
                                                <SelectAll
                                                    className={classnames('fa-duotone fa-check-double text-success')}
                                                    onClick={() => selectAllSubs(tabId)}
                                                />
                                            }
                                        </Header>
                                        <div
                                            className={`${!accessMap[tabId].selected && 'custom-disabled'}`}
                                        >
                                            {
                                                Object.keys(accessMap[tabId].subs).map((subId) => (
                                                    <div key={subId}>
                                                        <div
                                                            className={'d-flex hover-pointer'}
                                                            onClick={() => selectSub(tabId, subId)}
                                                        >
                                                            <SubTitleDiv

                                                            >
                                                                <h4>{accessMap[tabId].subs[subId].label}</h4>
                                                            </SubTitleDiv>
                                                            <SubIcon className={getChecked(accessMap[tabId].subs[subId].selected)} />
                                                        </div>
                                                        <FinalSubDiv>
                                                            {
                                                                Object.keys(accessMap[tabId].subs[subId].subs || {}).map((finalSubId) => (
                                                                    <div
                                                                        className={'hover-pointer d-flex'}
                                                                        onClick={() => selectFinalSub(tabId, subId, finalSubId)}
                                                                        key={finalSubId}
                                                                    >
                                                                        <FinalSubTitleDiv>
                                                                            <h5>- {accessMap[tabId].subs[subId].subs[finalSubId].label}</h5>
                                                                        </FinalSubTitleDiv>
                                                                        <SubIcon className={getChecked(accessMap[tabId].subs[subId].subs[finalSubId].selected)} />
                                                                    </div>
                                                                ))
                                                            }
                                                        </FinalSubDiv>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </CardWrapper>
                                ))
                            }
                        </Wrapper>
                    )
                }
            </ModalBody>
            <ExpandedFooter>
                {
                    restirctedAccess &&
                    <FooterContentContainer>
                        {
                            !viewProfiles && accessMap && profileId ? (
                                <ActionIcon
                                    type={'delete'}
                                    onClick={() => deleteAccessMap(profileId)}
                                />
                            ) :
                                <div></div>
                        }
                        <ActionIcon
                            type={'save'}
                            onClick={() => manageAccessMap()}
                        />
                    </FooterContentContainer>
                }
            </ExpandedFooter>
        </Modal >
    );
}

const getChecked = (selected: boolean | undefined) => selected ? 'fa-duotone fa-circle-check text-success' : 'fa-solid fa-circle-dashed';

const Wrapper = styled.div`
    display: flex;
    justify-content: space-around;
    flex-wrap: wrap;
    gap: 10px;
`;

const Header = styled.div`
    display: flex;
`;

const TitleDiv = styled.div`
    width: 240px;
`;

const CardIcon = styled.i`
    font-size: 30px;
    margin-top: 1px;
    margin-right: 5px;
`;

const SelectAll = styled.i`
    font-size: 30px;
    &:hover {
        cursor: pointer;
    }
`;

const SubTitleDiv = styled.div`
    width: 245px;
    display: flex;
`;

const SubIcon = styled.i`
    font-size: 20px;
    display: inline;
`;

const FinalSubDiv = styled.div`
    padding-left: 30px;
`;

const FinalSubTitleDiv = styled.div`
    width: 215px;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
    justify-content: space-between;
    display: flex;
`;

const FlexContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 500px;
`;

const AccessMapButton = styled.div`
    border-radius: 25px;
    background-color: #51c878;
    width: 100px;
    padding: 10px;
    text-align: center;

    &:hover {
        background-color: #3f9a5d;
        color: white;
        cursor: pointer;
    }
`;